import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import { RideConditionContract } from '@polaris-customerweb/sharedcomponents.core';
import {
    GeoBounds,
    RoutingHelpersService,
} from '@polaris-customerweb/sharedcomponents.shared';
import { isSet } from 'lodash-es';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RideCondition } from 'src/app/models/ride-condition';

@Injectable({ providedIn: 'root' })
export class RideConditionResource {
    private baseUrl: string;

    constructor(
        configService: ConfigService,
        private http: HttpClient,
        private routingHelpersService: RoutingHelpersService
    ) {
        this.baseUrl = routingHelpersService.combineUrl(
            configService.apiUrl.rideCommand,
            'geofeatures/ride-condition'
        );
    }

    reportAbuse(condition: RideCondition): Observable<void> {
        return this.http.put<undefined>(this.baseUrl, condition.toContract());
    }

    getRideConditionById(id: string): Observable<RideCondition> {
        const url = this.routingHelpersService.combineUrl(this.baseUrl, id);
        const params = new HttpParams().set('skipAuthorization', 'true');

        return this.http
            .get<RideConditionContract>(url, { params })
            .pipe(
                map(
                    (rideConditionContract) =>
                        new RideCondition(rideConditionContract)
                )
            );
    }

    getRideConditionsByLocation(
        lat: number,
        lng: number,
        dist: number,
        rideType: string,
        lastModified?: string,
        reportedAfter?: string
    ): Observable<RideCondition[]> {
        let params = new HttpParams()
            .set('rideType', rideType)
            .set('latitude', String(lat))
            .set('longitude', String(lng))
            .set('metersRadius', String(dist))
            .set('reportedAfter', String(reportedAfter))
            .set('skipAuthorization', 'true');

        if (isSet(lastModified)) {
            params = params.set('modifiedAfter', lastModified);
        }

        return this.http
            .get<RideConditionContract[]>(this.baseUrl, { params })
            .pipe(
                map((rideConditionContracts) =>
                    rideConditionContracts.map(
                        (contract) => new RideCondition(contract)
                    )
                )
            );
    }

    getRideConditionsByMultipleLocation(
        bounds: GeoBounds[],
        rideType: string,
        reportedAfter?: string,
        lastModified?: string
    ): Observable<RideCondition[]> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'multiple'
        );
        const requestedBounds = bounds.map((b) => {
            return {
                bottom: b.south,
                left: b.west,
                modifiedAfter: lastModified,
                right: b.east,
                top: b.north,
            };
        });
        const request = {
            bounds: requestedBounds,
            reportedAfter,
            rideType,
        };
        const params = new HttpParams().set('skipAuthorization', 'true');

        return this.http
            .post<RideConditionContract[]>(url, request, {
                params,
            })
            .pipe(
                map((rideConditionContracts) =>
                    rideConditionContracts.map((contract) => {
                        const rideCondition = new RideCondition(contract);
                        rideCondition.expiresTimestamp =
                            Date.now() +
                            RideCondition.TIME_TO_LIVE_SECONDS * 1000;

                        return rideCondition;
                    })
                )
            );
    }
}
